import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {runQueryOutputRowToTSV} from 'insights/analytics-queries/data/runQueryOutputToTSV';
import {ExperimentalShapashTableOptions} from 'machine-learning/handleClickShapashTableRow';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import ClipboardText from 'ui-library/ClipboardText';
import {Cell, CellRenderOptions, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {JSONValue} from 'util/Types';

import {RunQueryOutputLegacyData} from './AnalyticsWarehouseTypes';
import cellToString from './cellToString';

interface RunQueryOutputTableProps {
  // Render JSON value in a row of query results to a React node.
  // (RunQueryOutputTable will take care of wrapping it in <Cell> for SimpleTable.)
  //
  // Optional prop. By default this renders the value as string (aka raw output).
  renderCellContent?: (cell: JSONValue, options: CellRenderOptions) => ReactNode;
  renderHeader?: (headerName: string, headerIndex: number) => ReactNode;
  runQueryOutput: RunQueryOutputLegacyData;
  // Provide rowHasBeenInView boolean to renderCellContent function.
  // See SimpleTable documentation for details.
  // Comes with performance overhead linear with number of rows in the table.
  calculateInView?: boolean;
  experimentalShapashTableOptions?: ExperimentalShapashTableOptions;
}

function RunQueryOutputTable({
  renderCellContent = cellToString,
  runQueryOutput,
  renderHeader = (headerName: string) => headerName,
  calculateInView = false,
  experimentalShapashTableOptions,
}: RunQueryOutputTableProps): JSX.Element {
  const {headers, rows} = runQueryOutput;

  return (
    <>
      <Table
        calculateInView={calculateInView}
        className={css(styles.table)}
        emptyTableText="No rows"
        fitContent={true}
        frozenHeader={true}
        isCompact={true}
        numRows={rows.length}
        overflowVisible={true}
      >
        <Column
          renderCell={(rowIndex: number) => (
            <Cell>
              {BenchlingConfig.INSIGHTS_ENABLE_COPY_BUTTON_PER_ROW ? (
                <ClipboardText
                  clipboardData={runQueryOutputRowToTSV(rows[rowIndex])}
                  message="Row has been copied to the clipboard in TSV format."
                >
                  {rowIndex + 1}
                </ClipboardText>
              ) : (
                <>{rowIndex + 1}</>
              )}
            </Cell>
          )}
        />
        {headers.map((header, headerIndex) => (
          <Column
            key={headerIndex}
            header={<Cell className={css(styles.cell)}>{renderHeader(header, headerIndex)}</Cell>}
            renderCell={(rowIndex: number, options: CellRenderOptions) => (
              <Cell
                className={classNames(css(styles.cell, experimentalShapashTableOptions && styles.pointer))}
                onClick={
                  experimentalShapashTableOptions != null
                    ? () => {
                        experimentalShapashTableOptions.onClick(rows[rowIndex]);
                      }
                    : undefined
                }
              >
                {renderCellContent(rows[rowIndex][headerIndex], options)}
              </Cell>
            )}
          />
        ))}
      </Table>
    </>
  );
}

export default React.memo(RunQueryOutputTable);

const styles = StyleSheet.create({
  cell: FONT_PROXIMA_13,
  table: {
    borderRadius: 0,
    marginBottom: 0,
  },
  pointer: {
    cursor: 'pointer',
  },
});
